import { useMemo, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DefinitionTooltip } from "src/components/common";
import { useLanguage } from "src/hooks/useLanguage";
import { CHART_COLOR_PALETTE } from "src/components/charts/chartPalette";
import {
  bucketSocialSeries,
  type SocialDailyPoint,
  type SocialTrendGranularity,
} from "../../../data/organicSocialMockData";
import { getOrganicContentSectionDefinitionInfo } from "../organicMediaDefinitions";
import { SOCIAL_TREND_SERIES, type SocialValueFormat } from "./socialReportConfig";
import { formatSocialTrendDate, SocialTrendTooltip, getSocialTrendChartMargin, SOCIAL_TREND_Y_AXIS, renderSocialTrendCountAxisTick, renderSocialTrendPercentAxisTick } from "./socialReportChartParts";

interface SocialReportTrendsPanelProps {
  daily: SocialDailyPoint[];
}

const GRANULARITY_OPTIONS: SocialTrendGranularity[] = ["daily", "monthly"];

const SocialReportTrendsPanel: FC<SocialReportTrendsPanelProps> = ({ daily }) => {
  const { t, i18n } = useTranslation();
  const { isRTL } = useLanguage();
  const [granularity, setGranularity] = useState<SocialTrendGranularity>("daily");

  const data = useMemo(
    () =>
      bucketSocialSeries(daily, granularity).map(point => ({
        date: point.date,
        engagementRate: point.engagementRate ?? 0,
        views: point.views,
      })),
    [daily, granularity]
  );

  const tooltipFormats = useMemo(
    () =>
      SOCIAL_TREND_SERIES.reduce<Record<string, SocialValueFormat>>(
        (formats, series) => ({ ...formats, [series.key]: series.format }),
        {} as Record<string, SocialValueFormat>
      ),
    []
  );

  const title = t("organicMedia.social.sections.trends");

  return (
    <div className="organic-content-panel organic-content-panel--trends">
      <div className="organic-content-panel__header">
        <div className="organic-content-panel__title">
          <h3>{title}</h3>
          <DefinitionTooltip
            label={title}
            info={getOrganicContentSectionDefinitionInfo("trendsLast30Days", t)}
            formulaTitle={t("organicMedia.common.formula")}
          />
        </div>
        <div
          className="organic-content-panel__segmented"
          role="group"
          aria-label={t("organicMedia.contentReport.trendGranularity")}
        >
          {GRANULARITY_OPTIONS.map(option => (
            <button
              key={option}
              type="button"
              className={
                granularity === option
                  ? "organic-content-panel__segmented-button organic-content-panel__segmented-button--active"
                  : "organic-content-panel__segmented-button"
              }
              onClick={() => setGranularity(option)}
            >
              {t(`organicMedia.contentReport.${option}`)}
            </button>
          ))}
        </div>
      </div>
      <div className="organic-content-panel__trends-chart">
        <ResponsiveContainer width="100%" height={380}>
        <LineChart data={data} margin={getSocialTrendChartMargin(isRTL)}>
          <CartesianGrid stroke="#e4e8ef" strokeDasharray="4 4" />
          <XAxis
            dataKey="date"
            tick={{ fill: "#5e6a7d", fontSize: 12 }}
            tickFormatter={value => formatSocialTrendDate(value, i18n.language)}
            axisLine={{ stroke: "#d7dde7" }}
            tickLine={false}
            interval="preserveStartEnd"
            height={30}
          />
          <YAxis
            yAxisId="pct"
            tick={renderSocialTrendPercentAxisTick(i18n.language)}
            axisLine={{ stroke: "#d7dde7" }}
            tickLine={false}
            width={SOCIAL_TREND_Y_AXIS.pctWidth}
            tickMargin={SOCIAL_TREND_Y_AXIS.tickMargin}
          />
          <YAxis
            yAxisId="count"
            orientation="right"
            tick={renderSocialTrendCountAxisTick(i18n.language)}
            axisLine={{ stroke: "#d7dde7" }}
            tickLine={false}
            width={SOCIAL_TREND_Y_AXIS.countWidth}
            tickMargin={SOCIAL_TREND_Y_AXIS.tickMargin}
          />
          <Tooltip
            content={<SocialTrendTooltip formats={tooltipFormats} />}
            labelFormatter={(value: string | number) => formatSocialTrendDate(value, i18n.language)}
            allowEscapeViewBox={{ x: false, y: false }}
            position={{ y: 18 }}
          />
          {SOCIAL_TREND_SERIES.map((series, index) => (
            <Line
              key={series.key}
              yAxisId={series.format === "percent" ? "pct" : "count"}
              type="monotone"
              dataKey={series.key}
              name={t(series.labelKey)}
              stroke={CHART_COLOR_PALETTE[index % CHART_COLOR_PALETTE.length]}
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 4 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      </div>
      <div className="organic-content-panel__trend-legend" aria-hidden>
        {SOCIAL_TREND_SERIES.map((series, index) => (
          <div key={series.key} className="organic-content-panel__trend-legend-item">
            <span
              className={`organic-content-panel__trend-legend-swatch social-report__legend-swatch--palette-${index}`}
            />
            <span>{t(series.labelKey)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SocialReportTrendsPanel;
